/**
 * Sidebar — 240px-wide left nav with tenant switcher at the top, primary
 * nav items below, and a small build identity footer. Collapses to a
 * 56px-wide icon rail on narrow viewports / via the toggle button.
 */
import { useState, type ComponentType, type SVGProps } from 'react';
import { Link, useRouterState } from '@tanstack/react-router';
import {
  AlertTriangle,
  Building2,
  ChevronLeft,
  ChevronRight,
  FileText,
  Globe,
  LayoutGrid,
  Mail,
  Server,
  Settings,
} from './icons';
import { LamaIcon } from './LamaIcon';
import { TenantSwitcher, type TenantOption } from './TenantSwitcher';
import { useTenant } from '@/lib/tenant';
import { cn } from './cn';

type IconComp = ComponentType<SVGProps<SVGSVGElement> & { size?: number | string }>;

export interface NavItem {
  id: string;
  /** TanStack Router path the item links to. */
  to: string;
  label: string;
  icon: IconComp;
  /** Optional numeric/short-label badge (e.g. open alerts). */
  badge?: number | string;
  badgeKind?: 'neutral' | 'critical';
}

export interface SidebarProps {
  /** Tenants the current user may switch between. */
  tenants?: readonly TenantOption[];
  /** Whether the "Alle tenants" option appears (admin/operator). */
  allowAllTenants?: boolean;
  /** Override the default nav items. */
  items?: readonly NavItem[];
  /** Optional build identifier shown in the footer. */
  buildId?: string;
}

// Tenant-scoped drill-in nav. The data routes only exist under
// /tenants/$slug/* (there are no global /domains, /reports, … routes), so the
// section links point at the currently-selected tenant. With "Alle tenants"
// (no tenant) only the global items show; selecting a tenant in the switcher
// reveals that tenant's sections.
function buildNav(slug: string): readonly NavItem[] {
  const scoped = slug && slug !== 'all';
  const base = scoped ? `/tenants/${slug}` : null;
  const items: NavItem[] = [
    { id: 'global', to: base ?? '/', label: 'Dashboard', icon: LayoutGrid },
    { id: 'tenants', to: '/tenants', label: 'Tenants', icon: Building2 },
  ];
  if (base) {
    items.push(
      { id: 'domains', to: `${base}/domains`, label: 'Domeinen', icon: Globe },
      { id: 'reports', to: `${base}/reports`, label: 'Rapporten', icon: FileText },
      { id: 'sources', to: `${base}/sources`, label: 'Bronnen', icon: Server },
      { id: 'deliveries', to: `${base}/deliveries`, label: 'Verzonden', icon: Mail },
      { id: 'alerts', to: `${base}/alerts`, label: 'Alerts', icon: AlertTriangle },
    );
  }
  items.push({ id: 'admin', to: '/admin/users', label: 'Admin', icon: Settings });
  return items;
}

export function Sidebar({
  tenants = [],
  allowAllTenants = true,
  items,
  buildId,
}: SidebarProps): JSX.Element {
  const [collapsed, setCollapsed] = useState(false);
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const { slug } = useTenant();
  const navItems = items ?? buildNav(slug);

  return (
    <aside
      data-collapsed={collapsed}
      className={cn(
        'relative flex shrink-0 flex-col border-r border-border-subtle bg-surface transition-[width] duration-base ease-standard',
        collapsed ? 'w-sidebar-collapsed' : 'w-sidebar',
      )}
      aria-label="Hoofdnavigatie"
    >
      {/* Brand + collapse toggle */}
      <div className="flex h-topbar items-center justify-between gap-2 border-b border-border-subtle px-3">
        {collapsed ? (
          <div className="flex w-full items-center justify-center">
            <span
              className="flex h-6 w-6 items-center justify-center rounded bg-brand-600 text-fg-on-brand"
              aria-label="RuaHQ"
            >
              <LamaIcon size={16} />
            </span>
          </div>
        ) : (
          <div className="flex min-w-0 items-center gap-2">
            <span
              className="flex h-6 w-6 items-center justify-center rounded bg-brand-600 text-fg-on-brand"
              aria-hidden="true"
            >
              <LamaIcon size={16} />
            </span>
            <span className="truncate text-sm font-semibold text-fg-primary">RuaHQ</span>
          </div>
        )}
        <button
          type="button"
          aria-label={collapsed ? 'Zijbalk uitklappen' : 'Zijbalk inklappen'}
          aria-expanded={!collapsed}
          onClick={() => setCollapsed((c) => !c)}
          className="text-fg-muted transition hover:text-fg-primary"
        >
          {collapsed ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
        </button>
      </div>

      {/* Tenant switcher */}
      {!collapsed ? (
        <div className="border-b border-border-subtle px-3 py-2">
          <TenantSwitcher tenants={tenants} allowAll={allowAllTenants} />
        </div>
      ) : null}

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto py-3">
        <ul className="space-y-0.5 px-2">
          {navItems.map((item) => (
            <li key={item.id}>
              <NavLink item={item} collapsed={collapsed} pathname={pathname} />
            </li>
          ))}
        </ul>
      </nav>

      {/* Footer */}
      {!collapsed ? (
        <div className="border-t border-border-subtle px-3 py-2 text-xs text-fg-muted">
          <div className="flex items-center justify-between">
            <span>RuaHQ · v0.1</span>
            {buildId ? <span className="font-mono">{buildId}</span> : null}
          </div>
        </div>
      ) : null}
    </aside>
  );
}

interface NavLinkProps {
  item: NavItem;
  collapsed: boolean;
  pathname: string;
}

function NavLink({ item, collapsed, pathname }: NavLinkProps): JSX.Element {
  const Icon = item.icon;
  const active =
    item.to === '/' ? pathname === '/' : pathname === item.to || pathname.startsWith(`${item.to}/`);
  return (
    <Link
      to={item.to}
      title={collapsed ? item.label : undefined}
      aria-current={active ? 'page' : undefined}
      className={cn(
        'flex items-center gap-2.5 rounded px-2 py-1.5 text-sm transition-colors',
        active
          ? 'bg-brand-50 font-medium text-brand-700'
          : 'text-fg-secondary hover:bg-surface-subtle',
      )}
    >
      <Icon size={15} className="shrink-0" aria-hidden="true" />
      {!collapsed ? (
        <>
          <span className="min-w-0 flex-1 truncate">{item.label}</span>
          {item.badge != null ? (
            <span
              className={cn(
                'rounded px-1.5 py-0.5 font-mono text-xs',
                item.badgeKind === 'critical'
                  ? 'bg-fail-50 text-fail-700'
                  : 'bg-slate-100 text-fg-secondary',
              )}
            >
              {item.badge}
            </span>
          ) : null}
        </>
      ) : null}
    </Link>
  );
}
